<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { ApiError } from '@/utils/errors'
import AppIcon from '@/components/AppIcon.vue'

const auth = useAuthStore()
const ui = useUiStore()
const route = useRoute()
const router = useRouter()

const account = ref('')
const password = ref('')
const captcha = ref('')
const captchaCode = ref('')
const showPassword = ref(false)
const remember = ref(true)
const accountInput = ref<HTMLInputElement | null>(null)
const passwordInput = ref<HTMLInputElement | null>(null)
const captchaInput = ref<HTMLInputElement | null>(null)
const accountError = ref('')
const passwordError = ref('')
const captchaError = ref('')
const formError = ref('')
const expired = computed(() => route.query.expired === '1')
const redirect = computed(() => {
  const value = typeof route.query.redirect === 'string' ? route.query.redirect : '/dashboard'
  return value.startsWith('/') && !value.startsWith('//') ? value : '/dashboard'
})

function refreshCaptcha(): void {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  captchaCode.value = Array.from({ length: 4 }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('')
  captcha.value = ''
  captchaError.value = ''
}

function focusFirstInvalid(): void {
  nextTick(() => {
    if (accountError.value) accountInput.value?.focus()
    else if (passwordError.value) passwordInput.value?.focus()
    else if (captchaError.value) captchaInput.value?.focus()
  })
}

function validate(): boolean {
  if (auth.locked) {
    formError.value = `登录失败次数过多，账号已临时锁定，请在 ${Math.ceil(auth.lockRemaining / 60)} 分钟后重试`
    return false
  }
  accountError.value = account.value.trim() ? '' : '请输入管理员账号'
  passwordError.value = password.value.length >= 6 ? '' : '口令至少 6 位'
  const captchaOk = captcha.value.trim().toUpperCase() === captchaCode.value
  captchaError.value = captchaOk ? '' : '验证码不正确'
  if (!captchaOk) {
    const message = captchaError.value
    refreshCaptcha()
    captchaError.value = message
  }
  if (accountError.value || passwordError.value || captchaError.value) focusFirstInvalid()
  return !accountError.value && !passwordError.value && !captchaError.value
}

async function submit(): Promise<void> {
  if (auth.loading || auth.locked) return
  formError.value = ''
  if (!navigator.onLine) {
    formError.value = '网络已断开，请恢复网络后重新登录。'
    return
  }
  if (!validate()) return
  try {
    await auth.login(account.value, password.value, remember.value)
    ui.toast('success', `欢迎回来，${auth.profile?.name ?? '管理员'}`, '正在进入运营工作台。')
    await router.replace(redirect.value)
  } catch (error) {
    formError.value = error instanceof Error ? error.message : '登录未完成，请重试'
    if (error instanceof ApiError && error.code === 'offline') formError.value = '网络已断开，请恢复网络后重新登录。'
    refreshCaptcha()
    if (error instanceof ApiError && error.code === '422') {
      if (error.fieldErrors?.account) accountInput.value?.focus()
      else passwordInput.value?.focus()
    }
  }
}

onMounted(() => {
  document.title = '登录 · 康护健康'
  refreshCaptcha()
  if (expired.value) ui.toast('warning', '登录状态已过期', '为保护账号安全，请重新登录后继续。', 5200)
  if (auth.locked) formError.value = `登录失败次数过多，账号已临时锁定，请在 ${Math.ceil(auth.lockRemaining / 60)} 分钟后重试`
})
</script>

<template>
  <div class="login-page">
    <section class="login-brand" aria-label="康护健康">
      <div class="login-brand__top">
        <span class="brand__mark">
          <svg viewBox="0 0 48 48" width="24" height="24" fill="none" aria-hidden="true"><path d="M10 25h7l3-7 5 15 4-9h9" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
        </span>
        <div>
          <div class="login-brand__name">康护健康</div>
          <div class="login-brand__desc">家庭健康监测与照护服务平台</div>
        </div>
      </div>
      <div class="login-brand__body">
        <p class="login-eyebrow">运营工作台</p>
        <h1>把每一次健康提醒<br />落实到家庭照护中</h1>
        <p class="login-brand__text">统一管理用户与家庭档案、用药计划、健康预警、设备、内容和客服工单，让运营动作有据可循。</p>
        <ul class="login-points">
          <li><span><AppIcon name="shield" :size="16" /></span>角色权限与数据范围同源控制</li>
          <li><span><AppIcon name="alert" :size="16" /></span>健康预警按 SLA 跟进闭环</li>
          <li><span><AppIcon name="family" :size="16" /></span>家庭档案与守护关系联动</li>
        </ul>
      </div>
      <div class="login-brand__foot">
        <span>原型环境 · 内存数据</span>
        <span>Asia/Shanghai</span>
      </div>
      <div class="login-orbit login-orbit--one" aria-hidden="true" />
      <div class="login-orbit login-orbit--two" aria-hidden="true" />
    </section>

    <section class="login-panel">
      <div class="login-card">
        <div class="login-card__head">
          <h2>管理员登录</h2>
          <p>使用平台管理员账号进入运营工作台</p>
        </div>

        <div v-if="expired" class="info-banner info-banner--warn" role="status">
          <AppIcon name="clock" :size="14" />
          <span>会话已过期，重新登录后会返回你刚才访问的页面。</span>
        </div>

        <form class="login-form" novalidate @submit.prevent="submit">
          <label class="login-field" :class="{ 'has-error': accountError }">
            <span class="login-field__label">管理员账号</span>
            <span class="login-field__control">
              <AppIcon name="user" :size="16" />
              <input
                ref="accountInput"
                v-model="account"
                type="text"
                autocomplete="username"
                placeholder="请输入管理员账号"
                aria-label="管理员账号"
                :aria-invalid="Boolean(accountError)"
                @blur="accountError = account.trim() ? '' : '请输入管理员账号'"
              />
            </span>
            <span v-if="accountError" class="login-field__error" role="alert">{{ accountError }}</span>
          </label>

          <label class="login-field" :class="{ 'has-error': passwordError }">
            <span class="login-field__label">登录口令</span>
            <span class="login-field__control">
              <AppIcon name="lock" :size="16" />
              <input
                ref="passwordInput"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="请输入登录口令"
                aria-label="登录口令"
                :aria-invalid="Boolean(passwordError)"
              />
              <button type="button" class="login-field__eye" :aria-label="showPassword ? '隐藏口令' : '显示口令'" @click="showPassword = !showPassword">
                <AppIcon name="eye" :size="15" />
              </button>
            </span>
            <span v-if="passwordError" class="login-field__error" role="alert">{{ passwordError }}</span>
          </label>

          <label class="login-field" :class="{ 'has-error': captchaError }">
            <span class="login-field__label">图形验证码</span>
            <span class="login-captcha">
              <span class="login-field__control">
                <AppIcon name="shield" :size="15" />
                <input
                  ref="captchaInput"
                  v-model="captcha"
                  type="text"
                  inputmode="text"
                  maxlength="4"
                  autocomplete="off"
                  placeholder="请输入右侧验证码"
                  aria-label="图形验证码"
                  :aria-invalid="Boolean(captchaError)"
                  @keyup.enter="submit"
                />
              </span>
              <button type="button" class="login-captcha__code" aria-label="刷新验证码" title="点击刷新验证码" @click="refreshCaptcha">{{ captchaCode }}</button>
            </span>
            <span v-if="captchaError" class="login-field__error" role="alert">{{ captchaError }}</span>
          </label>

          <div v-if="auth.locked" class="login-lock" role="status">
            <AppIcon name="lock" :size="15" />
            <span>连续 5 次登录失败，当前会话已临时锁定。请在 {{ Math.ceil(auth.lockRemaining / 60) }} 分钟后重试；刷新页面会重置本次原型锁定计时。</span>
          </div>
          <div v-else-if="auth.failedAttempts" class="login-lock" role="status">
            <AppIcon name="info" :size="15" />
            <span>本次会话已失败 {{ auth.failedAttempts }} 次，剩余 {{ auth.attemptsRemaining }} 次机会。</span>
          </div>

          <div class="login-options">
            <label class="checkbox-line">
              <el-checkbox v-model="remember" aria-label="保持当前会话" />
              <span>保持当前会话</span>
            </label>
            <button type="button" class="text-button" @click="ui.toast('info', '口令找回说明', '原型环境不发送真实短信或邮件，请联系平台安全管理员重置口令。')">忘记口令？</button>
          </div>

          <div v-if="formError" class="login-error" role="alert">
            <AppIcon name="warning" :size="15" />
            <span>{{ formError }}</span>
          </div>

          <button type="submit" class="btn btn--primary login-submit" :disabled="auth.loading || auth.locked">
            <span v-if="auth.loading" class="login-spinner" aria-hidden="true" />
            <AppIcon v-else name="arrow-right" :size="15" />
            {{ auth.loading ? '正在验证账号…' : '进入运营工作台' }}
          </button>
        </form>

        <div class="login-hint">
          <AppIcon name="info" :size="14" />
          <p>原型使用内存管理员数据校验账号与口令长度，不连接真实认证服务，也不写入数据库。验收账号与口令见受保护的交付说明。</p>
        </div>
      </div>
      <footer class="login-foot">
        <span>康护健康 · 运营工作台</span>
        <span>版本信息仅在系统参数页展示</span>
      </footer>
    </section>
  </div>
</template>
